<template>
  <div class="widget-form-container">
    <div class="section-container">
      <substrate-mount>

        <div class="widget-options-container">
          <div class="row-container" @click="onToggleTopDonorsNumbersShown">
            <div class="widget-label">{{ $t('show.top') }}</div>
            <div v-if="isDesktop" class="widget-select">
              <div
                v-for="topDonorsNumber in topDonorsNumberList"
                :key="topDonorsNumber"
                class="widget-select-item"
                :class="{ active: topDonorsNumber === widget.topDonorsNumber }"
                @click="widget.topDonorsNumber = topDonorsNumber"
              >
                {{ topDonorsNumber }}
              </div>
            </div>
            <div v-else class="widget-select-container">
              <div class="widget-select-value">{{ widget.topDonorsNumber }}</div>
              <div
                class="arrow-icon"
                :class="{
                  'chevron-top-icon': isShownTopDonorsNumbers,
                  'chevron-bottom-icon': !isShownTopDonorsNumbers,
                }"
              ></div>
            </div>
          </div>

          <transition name="slide-vertical">
            <div v-if="isShownTopDonorsNumbers" class="select-variants-container">
              <div
                v-for="topDonorsNumber in topDonorsNumberList"
                :key="topDonorsNumber"
                class="select-variant-item"
                :class="{ active: topDonorsNumber === widget.topDonorsNumber }"
                @click="onChangeTopDonorsNumber(topDonorsNumber)"
              >
                {{ topDonorsNumber }}
              </div>
            </div>
          </transition>
        </div>

        <div class="widget-options-container">
          <div class="row-container" @click="onToggleTopDonorsTypesShown">
            <div class="widget-label">{{ $t('in.mode') }}</div>
            <div v-if="isDesktop" class="widget-select">
              <div
                v-for="({ key, value }) in topDonorsTypeList"
                :key="key"
                class="widget-select-item"
                :class="{ active: key === widget.topDonorsType }"
                @click="widget.topDonorsType = key"
              >
                {{ $t(value) }}
              </div>
            </div>
            <div v-else class="widget-select-container">
              <div class="widget-select-value">{{ $t(displayTopDonorsType) }}</div>
              <div
                class="arrow-icon"
                :class="{
                  'chevron-top-icon': isShownTopDonorsTypes,
                  'chevron-bottom-icon': !isShownTopDonorsTypes,
                }"
              ></div>
            </div>
          </div>

          <transition name="slide-vertical">
            <div v-if="isShownTopDonorsTypes" class="select-variants-container">
              <div
                v-for="topDonorsType in topDonorsTypeList"
                :key="topDonorsType.key"
                class="select-variant-item"
                :class="{ active: topDonorsType.key === widget.topDonorsType }"
                @click="onChangeTopDonorsType(topDonorsType.key)"
              >
                {{ $t(topDonorsType.value) }}
              </div>
            </div>
          </transition>
        </div>

        <div class="switcher-container">
          <cs-switcher v-model="widget.showSum" :label="$t('show.amounts')" />
        </div>
      </substrate-mount>
    </div>

    <div class="button-primary-container">
      <cs-button :width="buttonWidth" @click="onSubmit">
        {{ $t('common.proceed') }}
      </cs-button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import TopDonorsWidgetClass from 'services/widget/TopDonorsWidget';
import SubstrateMount from '@/components/capusta/SubstrateMount';

export default {
  name: 'TopDonorsWidget',
  components: {
    SubstrateMount,
  },
  props: {
    widgets: {
      type: Array,
      default: () => [],
    },
  },
  mounted() {
    this.fillingData();
  },
  data() {
    return {
      isShownTopDonorsNumbers: false,
      isShownTopDonorsTypes: false,
      widget: new TopDonorsWidgetClass,
    };
  },
  computed: {
    topDonorsNumberList() {
      return TopDonorsWidgetClass.topDonorsNumberList;
    },
    topDonorsTypeList() {
      return TopDonorsWidgetClass.topDonorsTypeList;
    },
    displayTopDonorsType() {
      return this.topDonorsTypeList.find(item => item.key === this.widget.topDonorsType).value;
    },
    buttonWidth() {
      return this.isDesktop ? 223 : '100%';
    },
    ...mapGetters(['isDesktop']),
  },
  methods: {
    onToggleTopDonorsNumbersShown() {
      if (this.isDesktop) {
        return;
      }

      this.isShownTopDonorsNumbers = !this.isShownTopDonorsNumbers;
    },
    onToggleTopDonorsTypesShown() {
      if (this.isDesktop) {
        return;
      }

      this.isShownTopDonorsTypes = !this.isShownTopDonorsTypes;
    },
    onChangeTopDonorsNumber(variant) {
      this.widget.topDonorsNumber = variant;
      this.isShownTopDonorsNumbers = false;
    },
    onChangeTopDonorsType(variant) {
      this.widget.topDonorsType = variant;
      this.isShownTopDonorsTypes = false;
    },
    onSubmit() {
      this.$emit('submit', this.widget.getData());
    },
    fillingData() {
      if (this.$validate.isEmptyArray(this.widgets)) {
        return false;
      }

      const findedData = this.widgets.find(widget => widget.type === this.widget.type);

      if (this.$validate.isUndefined(findedData)) {
        return false;
      }

      this.$nextTick(async () => {
        this.widget.setData(findedData);
      });
    },
  },
};
</script>

<style lang="sass" scoped>
  @import '~styles/components/widgets/widget-form'

  .row-container
    display: flex
    align-items: center
    justify-content: space-between
    margin-bottom: 10px
    padding: 5px 0

  .switcher-container
    +respond-to('medium')
      margin-bottom: 10px

  .widget-label
    color: #7CB342

  .widget-select-container
    display: flex
    align-items: center

  .widget-select-value
    margin-right: 20px
    font-weight: 500

  .arrow-icon
    width: 16px
    height: 16px
    background-repeat: no-repeat
    background-position: center
    background-size: cover
    transition: all .3s

  .chevron-bottom-icon
    background-image: url('~@/assets/svg/obsidian/arrows/chevron-bottom.svg')

  .chevron-top-icon
    background-image: url('~@/assets/svg/obsidian/arrows/chevron-top.svg')

  .select-variants-container
    margin-bottom: 20px
    display: flex
    flex-direction: column
    align-items: flex-start

  .select-variant-item
    width: 100%
    position: relative
    text-align: right
    background-color: transparent
    border-radius: 50px
    padding: 4px 20px
    border: 1px solid transparent
    -webkit-transition: all 0.16s
    transition: all 0.16s
    overflow: hidden

    &.active
      background: linear-gradient(232.25deg, rgba(91, 204, 126, 0.7) -8.32%, rgba(123, 211, 255, 0.7) 108.85%), rgba(255, 255, 255, 0.5)

  .widget-select
    display: flex

  .widget-select-item
    display: flex
    align-items: center
    padding: 5px 20px
    font-weight: 500
    font-size: 14px
    line-height: 20px
    border: 1px solid transparent
    border-radius: 50px
    cursor: pointer

    &:hover
      border: 1px solid #76BEB0

    &:not(:last-child)
      margin-right: 5px

    &.active
      color: #212121
      background: linear-gradient(249.76deg, #37B55E -8.32%, #7BD3FF 108.85%), #fff
      box-shadow: 0 0 4px rgba(0, 0, 0, 0.15)
</style>
